import React from 'react';
import { FiGlobe, FiTrendingUp,  FiClock, FiUsers, FiZap, FiShield } from 'react-icons/fi';

const CloudLMS = () => {
  const cloudBenefits = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Accessible from Anywhere, Anytime',
      description: 'Cloud LMS is available 24/7 from any device with internet access. Teachers can upload content from home. Students can learn from anywhere. Administrators can monitor progress from any location.',
      stat: '85% of institutions prefer cloud for remote accessibility',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'Automatic Updates',
      description: 'Cloud LMS updates automatically. New features, security patches, and improvements are applied instantly. No manual installations, no IT intervention, no downtime.',
      stat: '90% of cloud users appreciate automatic updates',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'No Infrastructure Costs',
      description: 'Cloud LMS requires no servers, no hardware, no IT infrastructure. Everything is hosted in the cloud. Institutions pay only for what they use.',
      stat: '70% cost savings on infrastructure with cloud LMS',
      color: 'bg-amber-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Scalable for Growth',
      description: 'Cloud LMS scales effortlessly. Add more students, more teachers, more courses without any hardware upgrades. Scale up or down as needed.',
      stat: '95% of institutions cite scalability as a key cloud benefit',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Enterprise-Grade Security',
      description: 'Cloud providers invest heavily in security—encryption, access controls, monitoring, and compliance. Most institutions cannot match this level of security on their own servers.',
      stat: '90% of cloud providers meet higher security standards',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Low Bandwidth Optimization',
      description: 'Cloud LMS platforms are optimized for low bandwidth environments. This is crucial for Indian institutions where internet connectivity can be inconsistent.',
      stat: '80% of Indian institutions value low bandwidth optimization',
      color: 'bg-blue-50'
    }
  ];

  const traditionalProblems = [
    {
      title: 'Limited to Campus Network',
      description: 'Traditional LMS is often only accessible on campus. Students and teachers cannot access materials from home without VPN or remote access setup.'
    },
    {
      title: 'Manual Updates Required',
      description: 'Every update must be installed manually. This requires IT staff, scheduling, and often involves downtime during business hours.'
    },
    {
      title: 'High Infrastructure Costs',
      description: 'Servers, storage, cooling, and IT staff. The cost of maintaining a traditional LMS is significantly higher than cloud.'
    },
    {
      title: 'Scaling is Difficult',
      description: 'Adding more students or courses requires hardware upgrades, which take time and money. Institutions often outgrow their systems.'
    },
    {
      title: 'Limited Accessibility',
      description: 'Traditional LMS often requires specific software, browsers, or devices. Compatibility issues are common.'
    },
    {
      title: 'Higher Maintenance Burden',
      description: 'IT staff spend significant time on maintenance, backups, and troubleshooting instead of strategic work.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The debate between cloud and traditional Learning Management Systems is one of the most important decisions educational institutions face today. The choice affects cost, accessibility, scalability, security, and the future of your institution's digital learning journey.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">Traditional LMS has been around for years.</strong> It requires servers, IT staff, and significant infrastructure investment. Cloud LMS is newer, but it's transforming how institutions deliver education.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here are the key differences between cloud LMS and traditional LMS—and why cloud is the future for Indian education.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">85%</div>
          <div className="text-sm text-slate-500">Prefer cloud for remote accessibility</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">70%</div>
          <div className="text-sm text-slate-500">Cost savings with cloud LMS</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">95%</div>
          <div className="text-sm text-slate-500">Value scalability as key benefit</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Cloud LMS is Transforming Indian Education</h2>

      {/* Cloud Benefits */}
      {cloudBenefits.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Problems with Traditional LMS */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Challenges of Traditional LMS</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {traditionalProblems.map((item, i) => (
            <div key={i} className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-start gap-2">
                <span className="text-red-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Cloud LMS vs. Traditional LMS</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional LMS</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Limited to campus network</span></li>
              <li className="flex items-start gap-2">• <span>Manual updates required</span></li>
              <li className="flex items-start gap-2">• <span>High infrastructure costs</span></li>
              <li className="flex items-start gap-2">• <span>Difficult to scale</span></li>
              <li className="flex items-start gap-2">• <span>Limited accessibility</span></li>
              <li className="flex items-start gap-2">• <span>IT maintenance burden</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Cloud LMS with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Accessible anywhere, anytime</span></li>
              <li className="flex items-start gap-2">• <span>Automatic updates</span></li>
              <li className="flex items-start gap-2">• <span>No infrastructure costs</span></li>
              <li className="flex items-start gap-2">• <span>Scalable for growth</span></li>
              <li className="flex items-start gap-2">• <span>Any device, any browser</span></li>
              <li className="flex items-start gap-2">• <span>Minimal IT involvement</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Indian Context */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">Why Cloud LMS is Perfect for Indian Institutions</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Indian institutions face unique challenges—limited IT budgets, unreliable internet in some areas, diverse user needs, and rapid growth. Cloud LMS addresses all these challenges.
        </p>
        <ul className="text-amber-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>No servers means no IT overhead</span></li>
          <li className="flex items-start gap-2">• <span>Low bandwidth optimization works with limited connectivity</span></li>
          <li className="flex items-start gap-2">• <span>Scale up as your institution grows</span></li>
          <li className="flex items-start gap-2">• <span>Students can access from home on any device</span></li>
          <li className="flex items-start gap-2">• <span>Affordable subscription pricing fits Indian budgets</span></li>
        </ul>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon's Cloud LMS is the Best Choice</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">☁️</div>
            <h4 className="font-bold text-slate-900 text-sm">100% Cloud-Based</h4>
            <p className="text-xs text-slate-600">No hardware, no IT overhead</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-First</h4>
            <p className="text-xs text-slate-600">Access from any device</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Low Bandwidth</h4>
            <p className="text-xs text-slate-600">Optimized for Indian networks</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Auto Updates</h4>
            <p className="text-xs text-slate-600">Always up-to-date</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Enterprise Security</h4>
            <p className="text-xs text-slate-600">Encrypted and secure</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💰</div>
            <h4 className="font-bold text-slate-900 text-sm">Affordable</h4>
            <p className="text-xs text-slate-600">Zero upfront cost</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">The Future of LMS is in the Cloud</h3>
        <p className="text-indigo-800 leading-relaxed">
          Traditional LMS is becoming obsolete. It's expensive, difficult to maintain, and limits accessibility. Cloud LMS is the future—accessible, affordable, scalable, and secure.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon's cloud LMS is built for Indian institutions. Mobile-first, low bandwidth optimized, and automatically updated. No servers, no IT overhead, no hassle.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for cloud LMS.</strong> The future of learning is in the cloud.
        </p>
      </div>
    </>
  );
};

export default CloudLMS;